import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UtensilsCrossed, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MenuGallery = () => {
  const [activeCategory, setActiveCategory] = useState('Semua');
  const [menus, setMenus] = useState([]);
  const [filteredMenus, setFilteredMenus] = useState([]);
  const [menuCategories, setMenuCategories] = useState(["Semua"]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // SEO Settings for Gallery
    document.title = "Menu Gallery Kenari Restaurant | Spencer Green Hotel Batu";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Jelajahi berbagai pilihan menu lezat di Kenari Restaurant, Spencer Green Hotel Batu. Mulai dari makanan khas Indonesia hingga dessert premium.');
    } else {
      const meta = document.createElement('meta');
      meta.name = "description";
      meta.content = "Jelajahi berbagai pilihan menu lezat di Kenari Restaurant, Spencer Green Hotel Batu. Mulai dari makanan khas Indonesia hingga dessert premium.";
      document.head.appendChild(meta);
    }

    const fetchData = async () => {
      try {
        const [menusRes, catsRes] = await Promise.all([
          axios.get(`${API_URL}/menus`),
          axios.get(`${API_URL}/menus/categories`)
        ]);
        setMenus(menusRes.data);
        setFilteredMenus(menusRes.data);
        
        const fetchedCats = catsRes.data.map(c => c.name);
        setMenuCategories(["Semua", ...fetchedCats]);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (activeCategory === 'Semua') {
      setFilteredMenus(menus);
    } else {
      setFilteredMenus(menus.filter(menu => menu.category === activeCategory));
    }
  }, [activeCategory, menus]);

  return (
    <div className="bg-emerald-50/30 min-h-screen pt-24 pb-16" data-testid="menu-gallery-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-12 h-[1px] bg-emerald-600" />
              <UtensilsCrossed className="w-5 h-5 text-emerald-600" />
              <div className="w-12 h-[1px] bg-emerald-600" />
            </div>
            <p className="text-emerald-600 uppercase tracking-widest text-sm mb-2">Our Menus</p>
            <h1 className="font-display text-4xl sm:text-5xl font-medium text-gray-900 mb-6">
              Kenari Restaurant Menu
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Temukan berbagai hidangan spesial yang diracik khusus oleh koki terbaik kami untuk memberikan pengalaman kuliner tak terlupakan.
            </p>
          </motion.div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {menuCategories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-emerald-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        )}

        {/* Menu Grid */}
        {!isLoading && (
          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence>
            {filteredMenus.map((menu) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                key={menu.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group flex flex-col"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                  <img
                    src={menu.image}
                    alt={menu.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  {menu.isRecommended && (
                    <div className="absolute top-4 right-4 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                      Recommended
                    </div>
                  )}
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-emerald-800 text-xs font-semibold px-3 py-1 rounded-full">
                    {menu.category}
                  </div>
                </div>
                
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex justify-between items-start mb-3">
                    <h2 className="font-display text-xl font-semibold text-gray-900 group-hover:text-emerald-700 transition-colors">
                      {menu.name}
                    </h2>
                    <span className="font-semibold text-emerald-600 ml-4 shrink-0">
                      {menu.price}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm line-clamp-3 mb-6 flex-grow">
                    {menu.description}
                  </p>
                  
                  <Link
                    to={`/menus/${menu.slug}`}
                    className="inline-flex items-center text-sm font-semibold text-emerald-600 hover:text-emerald-700 group/link mt-auto"
                  >
                    Lihat Detail
                    <ChevronRight className="w-4 h-4 ml-1 transition-transform group-hover/link:translate-x-1" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        )}

        {!isLoading && filteredMenus.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">Menu untuk kategori ini belum tersedia.</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default MenuGallery;
